import type { AppRouter } from "@repo/api";
import { getBaseUrl, isCanonicalHost } from "@repo/utils";
import { hc } from "hono/client";

/**
 * `getBaseUrl()` is absolute (NEXT_PUBLIC_SITE_URL), which is correct
 * everywhere except an agency's white-label client portal. A portal is served
 * from the agency's own hostname, so calling an absolute app URL from there
 * would be cross-origin: the browser would withhold the httpOnly `sg_portal`
 * cookie and the request would need CORS. Calling same-origin instead keeps
 * the cookie attached and keeps CORS out of the picture entirely — the API is
 * reachable on every host because `/api` is excluded from the middleware
 * matcher and therefore never rewritten into the portal route group.
 *
 * Only the browser can be on a portal host; on the server there is no `window`
 * and the absolute base stands.
 *
 * The environment is passed explicitly rather than left to `process.env`:
 * Next.js replaces `process.env.NEXT_PUBLIC_*` member expressions at build
 * time but does not hand the browser a populated `process.env` object, so the
 * default parameter would see no site URL and judge our own host non-canonical.
 */
const baseUrl =
	typeof window !== "undefined" &&
	!isCanonicalHost(window.location.hostname, {
		NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
		NEXT_PUBLIC_VERCEL_URL: process.env.NEXT_PUBLIC_VERCEL_URL,
		NEXT_PUBLIC_CANONICAL_HOSTS: process.env.NEXT_PUBLIC_CANONICAL_HOSTS,
	})
		? window.location.origin
		: getBaseUrl();

export const apiClient = hc<AppRouter>(baseUrl, {
	init: {
		credentials: "include",
	},
}).api;

/** Anything longer than this is a stack trace or an HTML page, not a reason. */
const MAX_ERROR_MESSAGE_LENGTH = 300;

/**
 * The server's explanation for a failed request, when it gave one.
 *
 * Routes answer in two shapes: `c.json({ error })` bodies and Hono's
 * `HTTPException`, whose default response body is the bare message as plain
 * text. Reading as text and parsing opportunistically covers both; anything
 * that looks like markup or is too long to be a sentence is discarded so the
 * caller falls back to its own copy.
 */
export async function readApiErrorMessage(
	response: Response,
): Promise<string | undefined> {
	const text = await response.text().catch(() => "");
	const trimmed = text.trim();
	if (!trimmed) {
		return undefined;
	}

	try {
		const body = JSON.parse(trimmed) as {
			error?: string;
			message?: string;
		};
		return body?.error || body?.message || undefined;
	} catch {
		// Plain-text HTTPException body
	}

	if (trimmed.startsWith("<") || trimmed.length > MAX_ERROR_MESSAGE_LENGTH) {
		return undefined;
	}
	return trimmed;
}
